import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, Chip, CircularProgress, IconButton,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, MenuItem,
  Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import EmailIcon from '@mui/icons-material/Email';
import BlockIcon from '@mui/icons-material/Block';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PaymentIcon from '@mui/icons-material/Payment';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import api from '../api';
import { conEmpresa } from '../empresa';
import { useEsMovil, tablaAncha } from '../estilos/responsivo';
import { pesos } from './Tile';
import { BLOQUE, CONTENEDOR_TABLA, TARJETA, TITULO_BLOQUE } from '../estilos/tarjeta';

const estadoColor = {
  borrador: { bg: '#f1f5f9', color: '#64748b' },
  emitida: { bg: '#dbeafe', color: '#2563eb' },
  pagada: { bg: '#d1fae5', color: '#059669' },
  parcial: { bg: '#fef3c7', color: '#d97706' },
  vencida: { bg: '#fee2e2', color: '#dc2626' },
  anulada: { bg: '#f1f5f9', color: '#94a3b8' },
};

export default function FacturaDetalle() {
  const esMovil = useEsMovil();
  const { id } = useParams();
  const navigate = useNavigate();
  const [factura, setFactura] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lineaDialog, setLineaDialog] = useState(false);
  const [pagoDialog, setPagoDialog] = useState(false);
  const [editLineaId, setEditLineaId] = useState(null);
  const [lineaForm, setLineaForm] = useState({ concepto: '', descripcion: '', cantidad: 1, precio_unitario: 0, descuento_pct: 0, tipo: 'otro' });
  const [pagoForm, setPagoForm] = useState({ monto: 0, metodo_pago: 'transferencia', referencia: '', notas: '' });
  const [saving, setSaving] = useState(false);
  const [dianStatus, setDianStatus] = useState(null);

  const fetchFactura = async () => {
    try {
      const [fRes, dRes] = await Promise.all([
        api.get(`/facturas/${id}`),
        api.get(`/facturas/${id}/dian`).catch(() => ({ data: { estado_dian: null } }))
      ]);
      setFactura(fRes.data);
      setDianStatus(dRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchFactura(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleAddLinea = () => {
    setEditLineaId(null);
    setLineaForm({ concepto: '', descripcion: '', cantidad: 1, precio_unitario: 0, descuento_pct: 0, tipo: 'otro' });
    setLineaDialog(true);
  };

  const handleEditLinea = (l) => {
    setEditLineaId(l.id);
    setLineaForm({ concepto: l.concepto, descripcion: l.descripcion || '', cantidad: l.cantidad, precio_unitario: l.precio_unitario, descuento_pct: l.descuento_pct || 0, tipo: l.tipo || 'otro' });
    setLineaDialog(true);
  };

  const handleSaveLinea = async () => {
    setSaving(true);
    try {
      if (editLineaId) {
        await api.put(`/facturas/${id}/lineas/${editLineaId}`, lineaForm);
      } else {
        await api.post(`/facturas/${id}/lineas`, lineaForm);
      }
      setLineaDialog(false);
      fetchFactura();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const handleDeleteLinea = async (lid) => {
    if (!window.confirm('Eliminar esta linea?')) return;
    try {
      await api.delete(`/facturas/${id}/lineas/${lid}`);
      fetchFactura();
    } catch (err) { console.error(err); }
  };

  const handleEmitir = async () => {
    if (!window.confirm('Emitir esta factura? No se podra editar despues.')) return;
    try {
      await api.post(`/facturas/${id}/emitir`);
      fetchFactura();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const handleAnular = async () => {
    if (!window.confirm('Anular esta factura?')) return;
    try {
      await api.post(`/facturas/${id}/anular`);
      fetchFactura();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const handleDescargarPDF = () => {
    window.open(conEmpresa(`${api.defaults.baseURL}/facturas/${id}/pdf`), '_blank');
  };

  const handleEnviarEmail = async () => {
    if (!window.confirm(`Enviar factura por email a ${factura.tercero?.email_facturacion}?`)) return;
    try {
      const res = await api.post(`/facturas/${id}/enviar-email`);
      alert(`Email enviado a ${res.data.destinatario}`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error enviando email');
    }
  };

  const handleGenerarDian = async () => {
    if (!window.confirm('Generar documento electronico DIAN (XML UBL 2.1)?')) return;
    try {
      setSaving(true);
      const res = await api.post(`/facturas/${id}/generar-dian`);
      setDianStatus({ estado_dian: res.data.estado_dian, cufe: res.data.cufe });
      alert(`XML generado. CUFE: ${res.data.cufe.substring(0, 20)}...`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error generando documento DIAN');
    } finally { setSaving(false); }
  };

  const handleDescargarXml = () => {
    window.open(conEmpresa(`${api.defaults.baseURL}/facturas/${id}/dian/xml`), '_blank');
  };

  const handleSavePago = async () => {
    setSaving(true);
    try {
      await api.post(`/facturas/${id}/pagos`, pagoForm);
      setPagoDialog(false);
      fetchFactura();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const handleDeletePago = async (pid) => {
    if (!window.confirm('Eliminar este pago?')) return;
    try {
      await api.delete(`/pagos/${pid}`);
      fetchFactura();
    } catch (err) { console.error(err); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;
  if (!factura) return <Typography color="error">Factura no encontrada</Typography>;

  const ec = estadoColor[factura.estado] || estadoColor.borrador;
  const esBorrador = factura.estado === 'borrador';

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3,
                 flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate('/facturas')}><ArrowBackIcon /></IconButton>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>{factura.numero_factura}</Typography>
            <Chip label={factura.estado.toUpperCase()} size="small" sx={{ fontWeight: 700, fontSize: 11, bgcolor: ec.bg, color: ec.color }} />
            {dianStatus?.estado_dian && (
              <Chip label={`DIAN: ${dianStatus.estado_dian.toUpperCase()}`} size="small"
                sx={{ fontWeight: 700, fontSize: 10,
                  bgcolor: dianStatus.estado_dian === 'aceptada' ? '#d1fae5' : dianStatus.estado_dian === 'rechazada' ? '#fee2e2' : '#f3e8ff',
                  color: dianStatus.estado_dian === 'aceptada' ? '#059669' : dianStatus.estado_dian === 'rechazada' ? '#dc2626' : '#7c3aed'
                }} />
            )}
          </Box>
          <Typography sx={{ color: '#64748b', fontSize: 13 }}>
            {factura.tercero?.razon_social} | Periodo: {factura.periodo_facturado || '—'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {esBorrador && (
            <Button variant="contained" startIcon={<SendIcon />} onClick={handleEmitir}
              sx={{ bgcolor: '#10b981', borderRadius: 1, textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#059669' } }}>
              Emitir
            </Button>
          )}
          {factura.pdf_url && (
            <>
              <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={handleDescargarPDF}
                sx={{ borderColor: '#ef4444', color: '#ef4444', borderRadius: 1, textTransform: 'none', fontWeight: 600 }}>
                PDF
              </Button>
              {factura.tercero?.email_facturacion && (
                <Button variant="outlined" startIcon={<EmailIcon />} onClick={handleEnviarEmail}
                  sx={{ borderColor: '#2563eb', color: '#2563eb', borderRadius: 1, textTransform: 'none', fontWeight: 600 }}>
                  Email
                </Button>
              )}
            </>
          )}
          {!esBorrador && factura.estado !== 'anulada' && !dianStatus?.cufe && (
            <Button variant="outlined" startIcon={<DescriptionIcon />} onClick={handleGenerarDian} disabled={saving}
              sx={{ borderColor: '#7c3aed', color: '#7c3aed', borderRadius: 1, textTransform: 'none', fontWeight: 600 }}>
              Generar DIAN
            </Button>
          )}
          {dianStatus?.cufe && (
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDescargarXml}
              sx={{ borderColor: '#059669', color: '#059669', borderRadius: 1, textTransform: 'none', fontWeight: 600 }}>
              XML
            </Button>
          )}
          {factura.estado !== 'anulada' && !esBorrador && (
            <Button variant="outlined" startIcon={<PaymentIcon />} onClick={() => { setPagoForm({ monto: factura.saldo_pendiente, metodo_pago: 'transferencia', referencia: '', notas: '' }); setPagoDialog(true); }}
              sx={{ borderColor: '#1e3a5f', color: '#1e3a5f', borderRadius: 1, textTransform: 'none', fontWeight: 600 }}>
              Registrar Pago
            </Button>
          )}
          {factura.estado !== 'anulada' && (
            <Button variant="outlined" startIcon={<BlockIcon />} onClick={handleAnular} size="small"
              sx={{ borderColor: '#ef4444', color: '#ef4444', borderRadius: 1, textTransform: 'none' }}>
              Anular
            </Button>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 1 }}>
        {/* Lineas */}
        <Paper elevation={0} sx={CONTENEDOR_TABLA}>
          <Box sx={{ p: TARJETA.relleno, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
            <Typography sx={TITULO_BLOQUE}>Lineas de Factura</Typography>
            {esBorrador && (
              <Button size="small" startIcon={<AddIcon />} onClick={handleAddLinea}
                sx={{ color: '#1e3a5f', textTransform: 'none', fontWeight: 600 }}>
                Agregar
              </Button>
            )}
          </Box>
          <TableContainer>
            <Table size="small" sx={tablaAncha(640)}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 11 }}>CONCEPTO</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 11 }} align="center">CANT.</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 11 }} align="right">P. UNIT.</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 11 }} align="right">DTO.%</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 11 }} align="right">SUBTOTAL</TableCell>
                  {esBorrador && <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 11 }} align="right">ACC.</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {factura.lineas.map((l) => (
                  <TableRow key={l.id} hover>
                    <TableCell>
                      <Typography sx={{ fontWeight: 500, fontSize: 13, color: '#1e293b' }}>{l.concepto}</Typography>
                      {l.descripcion && <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>{l.descripcion}</Typography>}
                      {l.tipo && l.tipo !== 'otro' && (
                        <Chip label={l.tipo} size="small" sx={{ height: 18, fontSize: 9, mt: 0.5, bgcolor: '#eff6ff', color: '#1e3a5f' }} />
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: 13 }}>{l.cantidad}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 13 }}>{pesos(l.precio_unitario)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 13, color: l.descuento_pct > 0 ? '#dc2626' : '#94a3b8' }}>
                      {l.descuento_pct > 0 ? `${l.descuento_pct}%` : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: 13 }}>{pesos(l.subtotal)}</TableCell>
                    {esBorrador && (
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleEditLinea(l)}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" onClick={() => handleDeleteLinea(l.id)}><DeleteIcon fontSize="small" sx={{ color: '#ef4444' }} /></IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {factura.lineas.length === 0 && (
                  <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', color: '#94a3b8', py: 3 }}>Sin lineas</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Totales */}
          <Box sx={{ p: 2, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 4,
                       flexWrap: 'wrap' }}>
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ color: '#64748b', fontSize: 12 }}>Subtotal</Typography>
                {(factura.descuento_global_pct || 0) > 0 && (
                  <Typography sx={{ color: '#dc2626', fontSize: 12 }}>Descuento {factura.descuento_global_pct}%</Typography>
                )}
                <Typography sx={{ color: '#64748b', fontSize: 12 }}>IVA {factura.impuesto_pct}%</Typography>
                <Divider sx={{ my: 0.5 }} />
                <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: 14 }}>TOTAL</Typography>
                {(factura.total_retenciones || 0) > 0 && (
                  <>
                    {(factura.retencion_fuente_pct || 0) > 0 && <Typography sx={{ color: '#64748b', fontSize: 11 }}>ReteFuente {factura.retencion_fuente_pct}%</Typography>}
                    {(factura.retencion_ica_pct || 0) > 0 && <Typography sx={{ color: '#64748b', fontSize: 11 }}>ReteICA {factura.retencion_ica_pct}%</Typography>}
                    {(factura.retencion_iva_pct || 0) > 0 && <Typography sx={{ color: '#64748b', fontSize: 11 }}>ReteIVA {factura.retencion_iva_pct}%</Typography>}
                    <Divider sx={{ my: 0.5 }} />
                    <Typography sx={{ fontWeight: 700, color: '#059669', fontSize: 14 }}>TOTAL A PAGAR</Typography>
                  </>
                )}
              </Box>
              <Box sx={{ textAlign: 'right', minWidth: 80 }}>
                <Typography sx={{ fontSize: 12 }}>{pesos(factura.subtotal)}</Typography>
                {(factura.descuento_global_pct || 0) > 0 && (
                  <Typography sx={{ fontSize: 12, color: '#dc2626' }}>-{pesos((factura.descuento_global_monto || 0))}</Typography>
                )}
                <Typography sx={{ fontSize: 12 }}>{pesos(factura.impuesto_monto)}</Typography>
                <Divider sx={{ my: 0.5 }} />
                <Typography sx={{ fontWeight: 700, color: '#1e3a5f', fontSize: 14 }}>{pesos(factura.total)}</Typography>
                {(factura.total_retenciones || 0) > 0 && (
                  <>
                    {(factura.retencion_fuente_pct || 0) > 0 && <Typography sx={{ fontSize: 11, color: '#64748b' }}>-{pesos((factura.retencion_fuente_monto || 0))}</Typography>}
                    {(factura.retencion_ica_pct || 0) > 0 && <Typography sx={{ fontSize: 11, color: '#64748b' }}>-{pesos((factura.retencion_ica_monto || 0))}</Typography>}
                    {(factura.retencion_iva_pct || 0) > 0 && <Typography sx={{ fontSize: 11, color: '#64748b' }}>-{pesos((factura.retencion_iva_monto || 0))}</Typography>}
                    <Divider sx={{ my: 0.5 }} />
                    <Typography sx={{ fontWeight: 700, color: '#059669', fontSize: 14 }}>{pesos((factura.total_a_pagar || 0))}</Typography>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Panel derecho: Info + Pagos */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Info */}
          <Paper elevation={0} sx={BLOQUE}>
            <Typography sx={{ ...TITULO_BLOQUE, mb: 1 }}>Informacion</Typography>
            {[
              { label: 'Cliente', value: factura.tercero?.razon_social },
              { label: 'Documento', value: factura.tercero ? `${factura.tercero.tipo_documento}: ${factura.tercero.numero_documento || '—'}` : '—' },
              { label: 'Emision', value: factura.fecha_emision ? new Date(factura.fecha_emision).toLocaleDateString() : '—' },
              { label: 'Vencimiento', value: factura.fecha_vencimiento ? new Date(factura.fecha_vencimiento).toLocaleDateString() : '—' },
              { label: 'Moneda', value: factura.moneda },
            ].map((item, i) => (
              <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                <Typography sx={{ color: '#64748b', fontSize: 12 }}>{item.label}</Typography>
                <Typography sx={{ fontWeight: 500, fontSize: 12, color: '#1e293b' }}>{item.value}</Typography>
              </Box>
            ))}
            {esBorrador && (
              <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #e2e8f0' }}>
                <Typography sx={{ color: '#64748b', fontSize: 11, fontWeight: 600, mb: 0.5 }}>DESCUENTO GLOBAL</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    size="small" type="number" value={factura.descuento_global_pct || 0}
                    onChange={async (e) => {
                      const val = parseFloat(e.target.value) || 0;
                      try {
                        await api.put(`/facturas/${id}`, { descuento_global_pct: val });
                        fetchFactura();
                      } catch (err) { console.error(err); }
                    }}
                    inputProps={{ min: 0, max: 100, step: 0.5 }}
                    sx={{ width: 80 }}
                  />
                  <Typography sx={{ color: '#64748b', fontSize: 12 }}>%</Typography>
                </Box>
              </Box>
            )}
            {!esBorrador && (factura.descuento_global_pct || 0) > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                <Typography sx={{ color: '#dc2626', fontSize: 12 }}>Descuento Global</Typography>
                <Typography sx={{ fontWeight: 500, fontSize: 12, color: '#dc2626' }}>{factura.descuento_global_pct}%</Typography>
              </Box>
            )}
            {esBorrador && (
              <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #e2e8f0' }}>
                <Typography sx={{ color: '#64748b', fontSize: 11, fontWeight: 600, mb: 0.5 }}>RETENCIONES</Typography>
                {[
                  { label: 'ReteFuente %', field: 'retencion_fuente_pct' },
                  { label: 'ReteICA %', field: 'retencion_ica_pct' },
                  { label: 'ReteIVA %', field: 'retencion_iva_pct' },
                ].map((ret) => (
                  <Box key={ret.field} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                    <Typography sx={{ color: '#64748b', fontSize: 11, width: 90 }}>{ret.label}</Typography>
                    <TextField
                      size="small" type="number" value={factura[ret.field] || 0}
                      onChange={async (e) => {
                        const val = parseFloat(e.target.value) || 0;
                        try {
                          await api.put(`/facturas/${id}`, { [ret.field]: val });
                          fetchFactura();
                        } catch (err) { console.error(err); }
                      }}
                      inputProps={{ min: 0, max: 100, step: 0.1 }}
                      sx={{ width: 70 }}
                    />
                  </Box>
                ))}
              </Box>
            )}
            {!esBorrador && (factura.total_retenciones || 0) > 0 && (
              <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #e2e8f0' }}>
                <Typography sx={{ color: '#64748b', fontSize: 11, fontWeight: 600, mb: 0.5 }}>RETENCIONES</Typography>
                {(factura.retencion_fuente_pct || 0) > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
                    <Typography sx={{ color: '#64748b', fontSize: 11 }}>ReteFuente {factura.retencion_fuente_pct}%</Typography>
                    <Typography sx={{ fontSize: 11 }}>-{pesos((factura.retencion_fuente_monto || 0))}</Typography>
                  </Box>
                )}
                {(factura.retencion_ica_pct || 0) > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
                    <Typography sx={{ color: '#64748b', fontSize: 11 }}>ReteICA {factura.retencion_ica_pct}%</Typography>
                    <Typography sx={{ fontSize: 11 }}>-{pesos((factura.retencion_ica_monto || 0))}</Typography>
                  </Box>
                )}
                {(factura.retencion_iva_pct || 0) > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
                    <Typography sx={{ color: '#64748b', fontSize: 11 }}>ReteIVA {factura.retencion_iva_pct}%</Typography>
                    <Typography sx={{ fontSize: 11 }}>-{pesos((factura.retencion_iva_monto || 0))}</Typography>
                  </Box>
                )}
              </Box>
            )}
          </Paper>

          {/* Resumen de pagos */}
          <Paper elevation={0} sx={BLOQUE}>
            <Typography sx={{ ...TITULO_BLOQUE, mb: 1 }}>Pagos</Typography>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
              <Typography sx={{ color: '#64748b', fontSize: 12 }}>Total Factura</Typography>
              <Typography sx={{ fontWeight: 600, fontSize: 12 }}>{pesos(factura.total)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
              <Typography sx={{ color: '#10b981', fontSize: 12 }}>Pagado</Typography>
              <Typography sx={{ fontWeight: 600, fontSize: 12, color: '#10b981' }}>{pesos(factura.total_pagado)}</Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 13, color: factura.saldo_pendiente > 0 ? '#f59e0b' : '#10b981' }}>
                Saldo Pendiente
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: 13, color: factura.saldo_pendiente > 0 ? '#f59e0b' : '#10b981' }}>
                {pesos(factura.saldo_pendiente)}
              </Typography>
            </Box>

            {factura.pagos.length > 0 && (
              <Box sx={{ mt: 1 }}>
                {factura.pagos.map((p) => (
                  <Box key={p.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, borderBottom: '1px solid #f1f5f9' }}>
                    <Box>
                      <Typography sx={{ fontSize: 12, fontWeight: 500 }}>{pesos(p.monto)}</Typography>
                      <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>
                        {p.metodo_pago} {p.referencia ? `- ${p.referencia}` : ''}
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => handleDeletePago(p.id)}>
                      <DeleteIcon fontSize="small" sx={{ color: '#ef4444', fontSize: 16 }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>

          {factura.notas && (
            <Paper elevation={0} sx={BLOQUE}>
              <Typography sx={{ ...TITULO_BLOQUE, mb: 0.5 }}>Notas</Typography>
              <Typography sx={{ color: '#64748b', fontSize: 13 }}>{factura.notas}</Typography>
            </Paper>
          )}

          {dianStatus?.cufe && (
            <Paper elevation={0} sx={BLOQUE}>
              <Typography sx={{ ...TITULO_BLOQUE, color: '#7c3aed', mb: 1 }}>Facturacion Electronica</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                <Typography sx={{ color: '#64748b', fontSize: 12 }}>Estado DIAN</Typography>
                <Chip label={dianStatus.estado_dian?.toUpperCase()} size="small"
                  sx={{ height: 20, fontSize: 10, fontWeight: 700,
                    bgcolor: dianStatus.estado_dian === 'aceptada' ? '#d1fae5' : dianStatus.estado_dian === 'rechazada' ? '#fee2e2' : '#f3e8ff',
                    color: dianStatus.estado_dian === 'aceptada' ? '#059669' : dianStatus.estado_dian === 'rechazada' ? '#dc2626' : '#7c3aed'
                  }} />
              </Box>
              <Box sx={{ py: 0.5 }}>
                <Typography sx={{ color: '#64748b', fontSize: 12 }}>CUFE</Typography>
                <Typography sx={{ fontSize: 10, color: '#1e293b', wordBreak: 'break-all', mt: 0.25, fontFamily: 'monospace', bgcolor: '#f8fafc', p: 0.5, borderRadius: 1 }}>
                  {dianStatus.cufe}
                </Typography>
              </Box>
              {dianStatus.created_at && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography sx={{ color: '#64748b', fontSize: 12 }}>Generado</Typography>
                  <Typography sx={{ fontSize: 12, color: '#1e293b' }}>
                    {new Date(dianStatus.created_at).toLocaleString()}
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
        </Box>
      </Box>

      {/* Dialog Linea */}
      <Dialog fullScreen={esMovil} open={lineaDialog} onClose={() => setLineaDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle>
          {editLineaId ? 'Editar Linea' : 'Nueva Linea'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 1 }}>
            <TextField label="Concepto *" value={lineaForm.concepto} onChange={(e) => setLineaForm({ ...lineaForm, concepto: e.target.value })}
              size="small" sx={{ gridColumn: { xs: 'auto', sm: 'span 2' } }} />
            <TextField label="Descripcion" value={lineaForm.descripcion} onChange={(e) => setLineaForm({ ...lineaForm, descripcion: e.target.value })}
              size="small" sx={{ gridColumn: { xs: 'auto', sm: 'span 2' } }} />
            <TextField label="Cantidad" value={lineaForm.cantidad} type="number"
              onChange={(e) => setLineaForm({ ...lineaForm, cantidad: parseFloat(e.target.value) || 0 })} size="small" />
            <TextField label="Precio Unitario" value={lineaForm.precio_unitario} type="number"
              onChange={(e) => setLineaForm({ ...lineaForm, precio_unitario: parseFloat(e.target.value) || 0 })} size="small" />
            <TextField label="Descuento %" value={lineaForm.descuento_pct} type="number"
              onChange={(e) => setLineaForm({ ...lineaForm, descuento_pct: parseFloat(e.target.value) || 0 })} size="small"
              inputProps={{ min: 0, max: 100, step: 0.5 }} />
            <TextField select label="Tipo" value={lineaForm.tipo}
              onChange={(e) => setLineaForm({ ...lineaForm, tipo: e.target.value })} size="small">
              {['plan', 'consumo_extra', 'numero_whatsapp', 'overhead', 'otro'].map(t => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </TextField>
            <Box sx={{ display: 'flex', alignItems: 'center', gridColumn: { xs: 'auto', sm: 'span 2' } }}>
              <Typography sx={{ color: '#64748b', fontSize: 13 }}>
                Bruto: {pesos((lineaForm.cantidad * lineaForm.precio_unitario))}
                {lineaForm.descuento_pct > 0 && (
                  <> — Dto: -{pesos(((lineaForm.cantidad * lineaForm.precio_unitario) * lineaForm.descuento_pct / 100))}</>
                )}
                {' '}= <strong>{pesos(((lineaForm.cantidad * lineaForm.precio_unitario) * (1 - (lineaForm.descuento_pct || 0) / 100)))}</strong>
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setLineaDialog(false)} sx={{ color: '#64748b' }}>Cancelar</Button>
          <Button onClick={handleSaveLinea} variant="contained" disabled={saving || !lineaForm.concepto}
            sx={{ bgcolor: '#1e3a5f', '&:hover': { bgcolor: '#2d5a87' } }}>
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Pago */}
      <Dialog fullScreen={esMovil} open={pagoDialog} onClose={() => setPagoDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle>Registrar Pago</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Monto *" value={pagoForm.monto} type="number"
              onChange={(e) => setPagoForm({ ...pagoForm, monto: parseFloat(e.target.value) || 0 })} size="small" />
            <TextField select label="Metodo de Pago" value={pagoForm.metodo_pago}
              onChange={(e) => setPagoForm({ ...pagoForm, metodo_pago: e.target.value })} size="small">
              {['transferencia', 'tarjeta', 'efectivo', 'stripe', 'otro'].map(m => (
                <MenuItem key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</MenuItem>
              ))}
            </TextField>
            <TextField label="Referencia" value={pagoForm.referencia}
              onChange={(e) => setPagoForm({ ...pagoForm, referencia: e.target.value })} size="small"
              placeholder="Numero de transaccion" />
            <TextField label="Notas" value={pagoForm.notas}
              onChange={(e) => setPagoForm({ ...pagoForm, notas: e.target.value })} size="small" multiline rows={2} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPagoDialog(false)} sx={{ color: '#64748b' }}>Cancelar</Button>
          <Button onClick={handleSavePago} variant="contained" disabled={saving || !pagoForm.monto}
            sx={{ bgcolor: '#1e3a5f', '&:hover': { bgcolor: '#2d5a87' } }}>
            {saving ? 'Registrando...' : 'Registrar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
